<template>
  <div
    class=" pt-[120px] pb-[100px] flex items-center justify-center p-4 relative overflow-hidden"
    style="background: url('/login-bg.webp'); background-repeat: no-repeat; background-size: cover"
  >
    <div class="absolute inset-0 "></div>

    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute top-1/4 left-1/4 w-2 h-2 bg-white/20 rounded-full animate-pulse"></div>
      <div class="absolute top-1/3 right-1/3 w-1 h-1 bg-blue-300/30 rounded-full animate-ping"></div>
      <div class="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-purple-300/20 rounded-full animate-bounce"></div>
    </div>

    <div class="relative bg-white/95 backdrop-blur-xl p-8 rounded-3xl shadow-2xl w-full max-w-md border border-white/20">

      <div class="text-center mb-8">
        <h2 class="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-2">
          {{ isLoginMode ? 'Bon retour !' : 'Bienvenue !' }}
        </h2>
        <p class="text-gray-500 text-sm leading-relaxed">
          {{ isLoginMode ? 'Connectez-vous pour accéder à votre espace' : 'Créez votre compte en quelques instants' }}
        </p>
      </div>

      <!-- Formulaires avec transitions -->
      <div class="transition-all duration-300 ease-in-out">
        <!-- Connexion -->
        <LoginForm
          v-if="isLoginMode"
          :is-loading="authStore.isLoading"
          :show-password="showPassword"
          :toggle-password-visibility="togglePasswordVisibility"
          @login-submit="handleLogin"
          @forgot-password-requested="showForgotPassword = true"
        />

        <!-- Inscription -->
        <RegisterForm
          v-else
          :is-loading="authStore.isLoading"
          :show-password="showPassword"
          :toggle-password-visibility="togglePasswordVisibility"
          @register-submit="handleRegister"
        />
      </div>

      <!-- Séparateur élégant -->
      <div class="mt-2 ">
        <div class="relative">
          <div class="absolute inset-0 flex items-center">
            <div class="w-full border-t border-gray-200"></div>
          </div>
          <div class="relative flex justify-center text-xs uppercase">
            <span class="bg-white px-3 text-gray-400 font-medium tracking-wide">Ou</span>
          </div>
        </div>
      </div>

      <div class="mt-6 text-center">
        <button
          @click="isLoginMode = !isLoginMode"
          class="group relative cursor-pointer w-full inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-transparent bg-gradient-to-r from-primary-50 to-blue-50 px-6 py-3 text-sm font-semibold text-gray-700 transition-all duration-200 hover:from-blue-100 hover:to-primary-100 hover:shadow-lg hover:shadow-blue-500/10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <svg
            class="w-4 h-4 transition-transform duration-200 group-hover:scale-110"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          {{ isLoginMode ? 'Créer un compte' : 'Se connecter' }}
          <div class="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 opacity-0 transition-opacity duration-200 group-hover:opacity-5"></div>
        </button>
      </div>

      <!-- Indicateur de sécurité -->
      <div class="mt-6 flex items-center justify-center gap-2 text-xs text-gray-400">
        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
        </svg>
        <span>Connexion sécurisée SSL</span>
      </div>
    </div>

    <!-- Modal mot de passe oublié -->
    <ForgotPasswordModal
      :show="showForgotPassword"
      :is-loading="authStore.isLoading"
      :initial-email="loginFormEmailForReset"
      @close="showForgotPassword = false; successMessage = ''; authStore.clearError()"
      @forgot-password-submit="handleForgotPassword"
      :success="successReset"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import LoginForm from '@/components/auth/loginForm.vue';
import RegisterForm from '@/components/auth/registerForm.vue';
import ForgotPasswordModal from '@/components/auth/ForgotPasswordModal.vue';
import { useToast } from '../../composables/useToast'
const { success, error, warning, info, toast, clear } = useToast()
import { useHead } from '@vueuse/head'

useHead({
  title: 'Inscription - Linguidia',
  meta: [
    {
      name: 'description',
      content: 'Rejoignez Linguidia et accédez à des outils modernes pour préparer le TCF Canada, TOEFL, IELTS, TOEIC et autres tests internationaux.'
    },
    { property: 'og:title', content: 'Inscription - Linguidia' },
    { property: 'og:description', content: 'Créez un compte Linguidia et commencez votre préparation personnalisée aux tests internationaux dès aujourd’hui.' },
    { property: 'og:type', content: 'website' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Inscription - Linguidia' },
    { name: 'twitter:description', content: 'Inscrivez-vous gratuitement et commencez votre préparation aux examens TOEFL, IELTS, TOEIC et plus encore.' },
    { name: 'twitter:image', content: '/images/linguidia-signup.jpg' }
  ],
  link: [
    { rel: 'canonical', href: 'https://linguidia.com/login' }
  ]
})

const router = useRouter();
const authStore = useAuthStore();

const isLoginMode = ref(true);
const showPassword = ref(false);
const showForgotPassword = ref(false);
const successMessage = ref('');
const redirectUrl = ref(null);
const successReset = ref(false)

const loginFormEmailForReset = ref('');

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

const handleLogin = async (formData) => {
  loginFormEmailForReset.value = formData.username;

  const result = await authStore.login({
    email: formData.username,
    password: formData.password
  });

  if (result.success) {
    successMessage.value = 'Connexion réussie ! \n Redirection en cours...';
    success(successMessage.value)

    setTimeout(()=>{
      if (redirectUrl.value == null){
        window.location.href = '/dashboard'
      }else{
        window.location.href = redirectUrl.value
      }
    },2000)

  } else {
    successMessage.value = '';
  }

  if (authStore.error) {
      error(authStore.error);
  }
};

const handleRegister = async (formData) => {
  const result = await authStore.register(formData);

  if (result.success) {
    successMessage.value = 'Inscription réussie ! Bienvenue';
    await authStore.login({email:formData.email, password:formData.password})
    await authStore.fetchUser()

    if (redirectUrl.value == null){
      window.location.href = "/dashboard/exercises?success=Inscription réussie ! Bienvenue"
    }else{
      window.location.href = redirectUrl.value
    }


  } else {
    successMessage.value = '';
    if (result.error?.username){
      error("Un compte existe déjà avec cette adresse email")
    }
  }
};

const handleForgotPassword = async (email) => {
  const result = await authStore.resetPassword(email);

  if (result.success) {
    successReset.value = true
    authStore.clearError();
    showForgotPassword.value = false;
    loginFormEmailForReset.value = '';
  } else {
    successMessage.value = '';
  }
};

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search)
  redirectUrl.value = urlParams.get('redirect');
  const authToken = urlParams.get('token');
  if (authToken){
    localStorage.setItem('access_token', authToken)
    router.push('/dashboard/profile');
    return
  }

  if (authStore.isAuthenticated) {
    router.push('/dashboard');
  }

});
</script>

<style scoped>
</style>
